import { useEffect, useRef } from "react";
import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { AppSidebar } from "@/components/shared/Sidebar";
import { UnifiedBottomNav } from "@/components/shared/UnifiedBottomNav";
import { SidebarProvider } from "@/components/ui/sidebar";
import { RoleTestBanner } from "@/components/account/RoleTestBanner";
import { useAuth } from "@/contexts/AuthContext";
import { navigateToAuth, hasStoredSession } from "@/utils/authRedirect";
import { AdminContentSkeleton } from "@/components/skeletons/AdminContentSkeleton";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { PageViewTracker } from "@/components/PageViewTracker";
import { logger } from "@/utils/logger";
import { RefreshCw } from "lucide-react";
import { LCommon } from "@/components/i18n";
import { useLocalizations } from "@/hooks/useLocalizations";
import { TYPOGRAPHY } from "@/design-system";

/**
 * UnifiedLayoutWrapper - Single layout for all authenticated routes
 * 
 * Visibility rules:
 * - Logged-in users: Sidebar always visible on all routes
 * - Guests: Sidebar only visible on /chat routes
 * - Protected routes: Redirect to auth if not logged in
 */
export function UnifiedLayoutWrapper() {
  const { lCommon } = useLocalizations();
  const { user, loading, authInitialized, authUnavailable, retryAuthentication } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Pre-warm role cache so ProtectedRoute doesn't flash a skeleton
  useUserRole();
  const isPreviewMode = searchParams.get('preview') === 'true';
  
  // Track if we've already checked for redirect to prevent multiple redirects
  const hasCheckedRedirect = useRef(false);
  
  // Determine if this is a chat or help-center route (accessible to guests)
  const isChatRoute = location.pathname.startsWith('/chat');
  const isHelpCenterRoute = location.pathname.startsWith('/help-center');
  
  // Determine if sidebar should be shown (for logged-in users OR guest-accessible routes)
  const showSidebar = !!user || isChatRoute;
  
  // Determine if this route requires authentication (help-center is guest-accessible)
  const requiresAuth = !isChatRoute && !isHelpCenterRoute && (
    location.pathname.startsWith('/account') || 
    location.pathname.startsWith('/admin')
  );

  useEffect(() => {
    // Only redirect for protected routes after auth has been fully initialized
    if (requiresAuth && authInitialized && !loading && !user && !isPreviewMode) {
      // Only suppress redirect if a valid (non-expired) session token exists in localStorage
      // This prevents false redirects during HMR while avoiding the stale-session deadlock
      if (hasStoredSession()) {
        logger.debug('[UnifiedLayout] Valid stored session found, waiting for auth restore');
        return;
      }
      
      // If URL hash contains access_token (email confirmation redirect),
      // don't redirect — let detectSessionInUrl process the tokens first
      if (window.location.hash && (
        window.location.hash.includes('access_token') ||
        window.location.hash.includes('type=signup') ||
        window.location.hash.includes('type=recovery')
      )) {
        return;
      }
      
      // Prevent multiple redirect attempts
      if (hasCheckedRedirect.current) {
        return;
      }
      hasCheckedRedirect.current = true;
      
      navigateToAuth(navigate, window.location.pathname + window.location.search);
    }
    
    // Reset redirect check when user becomes available
    if (user) {
      hasCheckedRedirect.current = false;
    }
  }, [authInitialized, loading, user, navigate, isPreviewMode, requiresAuth]);

  // Show skeleton during initial auth check for protected routes
  if (requiresAuth && (!authInitialized || loading || !user) && !isPreviewMode) {
    return (
      <SidebarProvider>
        <div className="h-screen flex flex-col w-full bg-background overflow-hidden">
          <Header />
          <div className="flex flex-1 min-h-0 overflow-hidden">
            <AppSidebar />
            <main className="flex-1 min-w-0 py-4 sm:py-6 lg:py-8 px-page-mobile sm:px-page-tablet lg:px-page pb-20 md:pb-4 overflow-y-auto">
              {authUnavailable ? (
                <div className="flex min-h-64 flex-col items-center justify-center gap-4 text-center">
                  <div>
                    <h1 className={TYPOGRAPHY.sectionTitle}>
                      <LCommon id="auth.session_check_delayed" defaultValue="Session check is taking longer than expected" />
                    </h1>
                    <p className={`mt-1 ${TYPOGRAPHY.cardDescription}`}>
                      <LCommon id="auth.session_preserved_retry" defaultValue="Your sign-in was kept. Try checking it again." />
                    </p>
                  </div>
                  <Button variant="outline" onClick={retryAuthentication} aria-label={lCommon('aria.retry_session_check', { defaultValue: 'Retry session check' })}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    <LCommon id="actions.retry" defaultValue="Retry" />
                  </Button>
                </div>
              ) : (
                <AdminContentSkeleton />
              )}
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Preview mode - simplified layout (content only, no Header/Footer/Sidebar)
  // NOTE: Suspense is handled at route level in App.tsx - no nested Suspense here
  if (isPreviewMode) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 py-4 px-4">
          <Outlet />
        </main>
      </div>
    );
  }

  // Chat route layout - optimized for full-height chat
  if (isChatRoute) {
    return (
      <SidebarProvider defaultOpen={true}>
        <div className="h-screen flex flex-col w-full bg-background overflow-hidden">
          <GoogleAnalytics />
          <PageViewTracker />
          <Header />
          
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {showSidebar && <AppSidebar />}
            <div className={`flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden ${user ? 'pb-16 md:pb-0' : ''}`}>
              <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
                <Outlet />
              </main>
            </div>
          </div>
          
          {user && <UnifiedBottomNav />}
        </div>
      </SidebarProvider>
    );
  }

  // Standard layout for account/admin routes
  return (
    <SidebarProvider>
      <div className="h-screen flex flex-col w-full bg-background overflow-hidden">
        <GoogleAnalytics />
        <PageViewTracker />
        <Header />
        {user && <RoleTestBanner />}
        
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {showSidebar && <AppSidebar />}
          {/* NOTE: Suspense is handled at route level in App.tsx - no nested Suspense here */}
          <main className="flex-1 min-w-0 py-4 sm:py-6 lg:py-8 px-page-mobile sm:px-page-tablet lg:px-page pb-20 md:pb-4 overflow-y-auto">
            <Outlet />
          </main>
        </div>
        
        {/* Mobile bottom navigation for logged-in users */}
        {user && <UnifiedBottomNav />}
        
        {/* Footer only for guests */}
        {!user && <Footer />}
      </div>
    </SidebarProvider>
  );
}
